import React, { useState, useEffect } from 'react';
import './Auth.css';

/**
 * Email-based Authentication with Turnstile
 * - Email code verification
 * - Cloudflare Turnstile bot protection
 * - Authorized users: docicu@gmail.com, harisbinushahashim@gmail.com
 */
const Auth = ({ onAuthenticated }) => {
  const [step, setStep] = useState('email'); // email, code, authenticated
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const AUTHORIZED_EMAILS = [
    'docicu@gmail.com',
    'harisbinushahashim@gmail.com'
  ];

  const TURNSTILE_SITE_KEY = '0x4AAAAAAB5n3d1__bFHtzti';

  // Load Turnstile script and handle callback
  useEffect(() => {
    // Define callback function
    window.onTurnstileSuccess = (token) => {
      setTurnstileToken(token);
    };

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      delete window.onTurnstileSuccess;
    };
  }, []);

  // Check if already authenticated
  useEffect(() => {
    const authData = localStorage.getItem('mcat_auth');
    if (authData) {
      try {
        const { email: savedEmail, timestamp } = JSON.parse(authData);
        // Auth valid for 30 days
        if (Date.now() - timestamp < 30 * 24 * 60 * 60 * 1000) {
          onAuthenticated(savedEmail);
          setStep('authenticated');
        } else {
          localStorage.removeItem('mcat_auth');
        }
      } catch (e) {
        localStorage.removeItem('mcat_auth');
      }
    }
  }, [onAuthenticated]);

  const generateCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!AUTHORIZED_EMAILS.includes(email.toLowerCase())) {
      setError('This email is not authorized. Please contact the administrator.');
      return;
    }

    // Check Turnstile
    if (!turnstileToken) {
      setError('Please complete the security check.');
      return;
    }

    setLoading(true);

    // Generate 6-digit code
    const verificationCode = generateCode();
    setSentCode(verificationCode);

    // In production, this would send email via backend
    // For now, show code in console (development)
    console.log(`🔐 Verification code for ${email}: ${verificationCode}`);
    
    // Simulate email sending
    setTimeout(() => {
      setLoading(false);
      setStep('code');
      alert(`Development Mode: Your verification code is ${verificationCode}\n\nIn production, this will be sent to ${email}`);
    }, 1000);
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    setError('');

    if (code === sentCode) {
      // Save authentication
      const authData = {
        email: email,
        timestamp: Date.now()
      };
      localStorage.setItem('mcat_auth', JSON.stringify(authData));
      
      setStep('authenticated');
      onAuthenticated(email);
    } else {
      setError('Invalid code. Please try again.');
      setCode('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mcat_auth');
    setStep('email');
    setEmail('');
    setCode('');
    setSentCode('');
    setTurnstileToken('');
  };

  if (step === 'authenticated') {
    return null; // Auth successful, show main app
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>🎓 MCAT Study Platform</h1>
          <p>Secure Access Required</p>
        </div>

        {step === 'email' && (
          <form onSubmit={handleSendCode} className="auth-form">
            <h2>Enter Your Email</h2>
            <p className="auth-description">
              Enter your authorized email to receive a verification code
            </p>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@gmail.com"
                required
                autoFocus
              />
            </div>

            {/* Turnstile Widget */}
            <div className="turnstile-container">
              <div
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-callback="onTurnstileSuccess"
                data-theme="light"
              ></div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading || !turnstileToken} className="auth-button">
              {loading ? 'Sending Code...' : 'Send Verification Code'}
            </button>

            <div className="auth-info">
              <p>✅ Authorized users only</p>
              <p>🔒 Protected by Cloudflare Turnstile</p>
            </div>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyCode} className="auth-form">
            <h2>Enter Verification Code</h2>
            <p className="auth-description">
              We sent a 6-digit code to <strong>{email}</strong>
            </p>

            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                required
                autoFocus
                className="code-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="auth-button">
              Verify Code
            </button>

            <button
              type="button"
              onClick={() => setStep('email')}
              className="auth-button-secondary"
            >
              ← Back to Email
            </button>

            <div className="auth-info">
              <p>Didn't receive the code? Check your spam folder</p>
            </div>
          </form>
        )}
      </div>

    </div>
  );
};

export default Auth;
